import { useState, useEffect, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useOrg } from '@/providers';
import { Button } from '@/components/ui';
import { SettingsPageLayout, SettingsPageSidebar } from '@/components/layout/settings-page-layout';
import { PageCanvas } from '@/components/layout/page-canvas';
import {
  useGetServiceCategories,
  usePutServiceCategoriesReorder,
  getGetServiceCategoriesQueryKey,
} from '@/api/generated/service-categories/service-categories';
import {
  useGetServicesServiceId,
  usePutServicesServiceIdReorder,
} from '@/api/generated/services/services';
import { SortableServiceCategoryList } from './services/layout/sortable-service-category-list';
import { NewCategoryForm } from './services/forms/new-category-form';
import { NewServiceForm } from './services/forms/new-service-form';
import { CategoryDetailView } from './services/layout/category-detail-view';
import { ServiceDetailView } from './services/layout/service-detail-view';
import { ServiceDetailSkeleton } from './services/layout/service-detail-skeleton';
import { useSkeletonUntilFresh } from '@/hooks/use-query-loading-state';
import { useDetailSelectionSearchParam } from '@/hooks/use-detail-selection-search-param';
import { useMediaQuery } from '@/hooks/use-media-query';
import { BREAKPOINT_QUERY } from '@/hooks/breakpoint-queries';
import type {
  ServicesContentView,
  CategoryDetailTab,
  ServiceDetailTab,
  CategoryDetail,
  ServiceDetail,
  CreatedServiceInfo,
} from './services/types';
import { formatDocumentTitle } from '@/lib/document-title';
import { useMenuImportAvailability } from './services/menu-import/use-menu-import-queries';
import { SERVICES_PAGE_CATEGORY_PARAMS } from './services/constants';
import { ImportMenuButton } from './services/menu-import/import-menu-button';
import { ImportMenuEmptyStateCard } from './services/menu-import/import-menu-empty-state-card';
import { ImportMenuDialog } from './services/menu-import/import-menu-dialog';
import { MenuImportStatusBanner } from './services/menu-import/menu-import-status-banner';
import { SameTimeSetupLinkButton } from './services/components/same-time-zone-setup/same-time-setup-link-button';
import { toServiceDetail } from './services/utils/to-service-detail';

const SERVICE_DETAIL_TABS: ServiceDetailTab[] = [
  'details',
  'staff',
  'resources',
  'customizations',
  'online-booking',
  'turn-tracker',
];

function isServiceDetailTab(value: unknown): value is ServiceDetailTab {
  return typeof value === 'string' && SERVICE_DETAIL_TABS.includes(value as ServiceDetailTab);
}

/** The open service lives in the URL (`?service=<id>`, so phone Back returns to the list); `&tab=` deep-links a tab. */
interface ServicesSearch {
  service?: string;
  tab?: ServiceDetailTab;
}

export const Route = createFileRoute('/_auth/$orgId/settings/services')({
  validateSearch: (search: Record<string, unknown>): ServicesSearch => ({
    service: typeof search.service === 'string' ? search.service : undefined,
    tab: isServiceDetailTab(search.tab) ? search.tab : undefined,
  }),
  component: ServicesSettingsPage,
});

function ServicesSettingsPage() {
  useOrg(); // Ensure we're in org context
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const navigateSearch = Route.useNavigate();
  // From lg the list and the detail sit side by side; below it one pane shows at a time.
  const isDesktop = useMediaQuery(BREAKPOINT_QUERY.lgUp);

  // Content view state
  const [contentView, setContentView] = useState<ServicesContentView>('default');

  // Selected category/service for detail view
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

  // Selected category detail for CategoryDetailView
  const [selectedCategory, setSelectedCategory] = useState<CategoryDetail | null>(null);

  // Active tab in category detail view
  const [activeCategoryTab, setActiveCategoryTab] = useState<CategoryDetailTab>('details');

  // Selected service detail for ServiceDetailView
  const [selectedService, setSelectedService] = useState<ServiceDetail | null>(null);

  // Active tab in service detail view
  const [activeServiceTab, setActiveServiceTab] = useState<ServiceDetailTab>('details');

  // Fetch service categories with nested services from API
  const { data: categoriesResponse, isLoading } = useGetServiceCategories(SERVICES_PAGE_CATEGORY_PARAMS);
  const categories = useMemo(() => categoriesResponse?.data ?? [], [categoriesResponse?.data]);

  // AI menu import: shown to salon-admin-equivalent users when the AI is configured.
  const menuImport = useMenuImportAvailability();
  const [isImportMenuOpen, setIsImportMenuOpen] = useState(false);
  const services = useMemo(
    () => categories.flatMap((category) => category.services ?? []),
    [categories]
  );
  // Only once categories loaded: a failed/pending request (no data) must not look like "no services".
  const hasOnlyDemoServices =
    categoriesResponse !== undefined && services.every((service) => service.isDemo);
  const navigate = useNavigate();
  const { orgId: routeOrgId } = Route.useParams();
  const handleMenuDraftReady = () => {
    setIsImportMenuOpen(false);
    void navigate({ to: '/$orgId/settings/services/import', params: { orgId: routeOrgId } });
  };

  // `?service=` ⇄ the open service: deep links, browser Back/Forward, and phone Back to the list.
  const serviceSelection = useDetailSelectionSearchParam({
    urlId: search.service ?? null,
    retryKey: categories,
    openFromUrl: (serviceId) => {
      const owningCategory = categories.find((category) =>
        category.services?.some((service) => service.id === serviceId)
      );
      if (!owningCategory) return false;
      setSelectedCategoryId(owningCategory.id);
      setSelectedServiceId(serviceId);
      setSelectedCategory(null);
      setContentView('service-detail');
      if (search.tab) setActiveServiceTab(search.tab);
      return true;
    },
    closeFromUrl: () => {
      if (contentView !== 'service-detail') return;
      setSelectedServiceId(null);
      setSelectedCategoryId(null);
      setContentView('default');
    },
    writeUrl: (serviceId, { replace, state }) =>
      void navigateSearch({
        search: (previous) => ({ ...previous, service: serviceId ?? undefined, tab: undefined }),
        replace,
        // `HistoryState` lives in @tanstack/history, which this app can't augment.
        state: () => state as never,
      }),
  });

  // Reorder mutations with optimistic updates to prevent snap-back
  const { mutate: reorderCategories } = usePutServiceCategoriesReorder({
    mutation: {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await queryClient.cancelQueries({
          queryKey: getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
        });

        // Snapshot current data for rollback
        const previousData = queryClient.getQueryData(
          getGetServiceCategoriesQueryKey({ includeServices: 'true' })
        );

        // Optimistically update cache with new order
        queryClient.setQueryData(
          getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
          (old: typeof categoriesResponse) => {
            if (!old?.data) return old;
            const { categoryIds } = variables.data;
            const reordered = categoryIds
              .map((id) => old.data.find((c) => c.id === id))
              .filter(Boolean);
            return { ...old, data: reordered };
          }
        );

        return { previousData };
      },
      onError: (_err, _variables, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(
            getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
            context.previousData
          );
        }
      },
      onSettled: () => {
        // Refetch after mutation settles to ensure consistency
        queryClient.invalidateQueries({
          queryKey: getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
        });
      },
    },
  });

  const { mutate: reorderService } = usePutServicesServiceIdReorder({
    mutation: {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
        });

        // Snapshot for rollback
        const previousData = queryClient.getQueryData(
          getGetServiceCategoriesQueryKey({ includeServices: 'true' })
        );

        // Optimistically update cache - move service to new position
        queryClient.setQueryData(
          getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
          (old: typeof categoriesResponse) => {
            if (!old?.data) return old;
            const { categoryId, position } = variables.data;
            const newData = old.data.map((category) => {
              if (category.id !== categoryId || !category.services) return category;
              const services = [...category.services];
              const currentIndex = services.findIndex((s) => s.id === variables.serviceId);
              if (currentIndex === -1) return category;
              const [movedService] = services.splice(currentIndex, 1);
              services.splice(position, 0, movedService);
              return { ...category, services };
            });
            return { ...old, data: newData };
          }
        );

        return { previousData };
      },
      onError: (_err, _variables, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(
            getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
            context.previousData
          );
        }
      },
      onSettled: () => {
        // Refetch after mutation settles
        queryClient.invalidateQueries({
          queryKey: getGetServiceCategoriesQueryKey({ includeServices: 'true' }),
        });
      },
    },
  });

  // Fetch full service details when a service is selected
  const serviceQuery = useGetServicesServiceId(selectedServiceId ?? '', {
    query: { enabled: !!selectedServiceId },
  });
  const serviceResponse = serviceQuery.data;
  // `selectedService` is copied from the query in an effect below, so for a moment after a click it
  // still holds the previously open service. Only show it once it IS the selected service and that
  // service wasn't opened on a stale cache that is still refetching.
  const isServiceDetailLoading = useSkeletonUntilFresh(serviceQuery, selectedServiceId);
  const visibleService =
    selectedService && selectedService.id === selectedServiceId && !isServiceDetailLoading
      ? selectedService
      : null;

  // Update selectedService when API data arrives
  useEffect(() => {
    if (serviceResponse?.data && selectedServiceId && selectedCategoryId) {
      const category = categories.find((c) => c.id === selectedCategoryId);
      setSelectedService(toServiceDetail(serviceResponse.data, category?.name ?? null));
    }
  }, [serviceResponse?.data, selectedServiceId, selectedCategoryId, categories]);

  // Handle Add Category button click
  const handleAddCategoryClick = () => {
    serviceSelection.clear();
    setSelectedCategoryId(null);
    setSelectedServiceId(null);
    setContentView('new-category');
  };

  // Handle cancel from new category form
  const handleCancelNewCategory = () => {
    setContentView('default');
  };

  // Handle successful category creation - go to default view
  // The user can then click the new category in the sidebar to view details
  const handleCategoryCreated = (_categoryId: string) => {
    setSelectedCategoryId(null);
    setSelectedServiceId(null);
    setSelectedCategory(null);
    setContentView('default');
  };

  // Handle category click
  const handleCategoryClick = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    if (category) {
      const categoryDetail: CategoryDetail = {
        id: category.id,
        name: category.name,
        sortOrder: category.sortOrder,
        isDemo: category.isDemo,
        onlyShowWithDirectLink: category.onlyShowWithDirectLink ?? false,
        isActive: category.isActive ?? true,
        createdAt: category.createdAt,
        updatedAt: category.updatedAt,
      };
      serviceSelection.clear();
      setSelectedCategory(categoryDetail);
      setSelectedCategoryId(categoryId);
      setSelectedServiceId(null);
      setContentView('category-detail');
    }
  };

  // Handle category updated
  const handleCategoryUpdated = (updatedCategory: CategoryDetail) => {
    setSelectedCategory(updatedCategory);
  };

  // Handle category deleted
  const handleCategoryDeleted = () => {
    setSelectedCategory(null);
    setSelectedCategoryId(null);
    setContentView('default');
  };

  // Handle service click
  const handleServiceClick = (serviceId: string, categoryId: string) => {
    // Set IDs which will trigger the useEffect to fetch full service data
    setSelectedCategoryId(categoryId);
    setSelectedServiceId(serviceId);
    setSelectedCategory(null);
    setContentView('service-detail');
    serviceSelection.select(serviceId);
  };

  // Handle add service to category
  const handleAddServiceToCategory = (categoryId: string) => {
    serviceSelection.clear();
    setSelectedCategoryId(categoryId);
    setSelectedServiceId(null);
    setContentView('new-service');
  };

  // Handle cancel from new service form
  const handleCancelNewService = () => {
    setContentView('default');
    setSelectedCategoryId(null);
  };

  // Handle successful service creation - go to default view
  const handleServiceCreated = (_serviceInfo: CreatedServiceInfo) => {
    setSelectedCategoryId(null);
    setSelectedServiceId(null);
    setContentView('default');
  };

  // Handle service updated
  const handleServiceUpdated = (updatedService: ServiceDetail) => {
    setSelectedService(updatedService);
  };

  // Handle service deleted
  const handleServiceDeleted = () => {
    serviceSelection.clear();
    setSelectedService(null);
    setSelectedServiceId(null);
    setSelectedCategoryId(null);
    setContentView('default');
  };

  // Phone/tablet Back to the list: an open service goes through the URL, the other views are local.
  const handleMobileBack = () => {
    if (contentView === 'service-detail') {
      serviceSelection.back();
      return;
    }
    setSelectedCategory(null);
    setSelectedCategoryId(null);
    setSelectedServiceId(null);
    setContentView('default');
  };

  // Get selected category name for new service form
  const getSelectedCategoryName = (): string => {
    if (!selectedCategoryId) return '';
    const category = categories.find((c) => c.id === selectedCategoryId);
    return category?.name ?? '';
  };

  // Handle category reorder via drag and drop
  const handleCategoriesReorder = (categoryIds: string[]) => {
    reorderCategories({ data: { categoryIds } });
  };

  // Handle service reorder within category via drag and drop
  const handleServiceReorder = (serviceId: string, categoryId: string, newIndex: number) => {
    reorderService({
      serviceId,
      data: {
        categoryId,
        position: newIndex,
      },
    });
  };

  // Sidebar content
  const sidebarContent = (
    <SettingsPageSidebar
      actionButton={
        // Desktop: a column of full-width pills. Phone/tablet: one compact row; Same-time is a list row below.
        <div className="mb-4 flex flex-wrap gap-2 lg:mb-5 lg:flex-col">
          <Button
            variant="action"
            shape="pill"
            fullWidth={isDesktop}
            leftIcon={<Plus aria-hidden="true" />}
            className="min-h-11 bg-clovie-primary text-[16px] font-semibold text-white shadow-soft transition-colors hover:bg-clovie-primary-hover focus-visible:ring-clovie-brand focus-visible:ring-offset-clovie-surface lg:text-base"
            onClick={handleAddCategoryClick}
          >
            Add Category
          </Button>
          {menuImport.enabled && (
            <ImportMenuButton fullWidth={isDesktop} onClick={() => setIsImportMenuOpen(true)} />
          )}
          {isDesktop && <SameTimeSetupLinkButton routeOrgId={routeOrgId} />}
        </div>
      }
    >
      {!isDesktop && (
        <>
          {/* The list is the whole screen here, so the import status shows above it (beside it on desktop). */}
          <div className="px-5">
            <MenuImportStatusBanner
              currentImport={menuImport.openImport}
              aiEnabled={menuImport.enabled}
              ownerRecipients={menuImport.ownerRecipients}
              routeOrgId={routeOrgId}
              onOpenImportDialog={() => setIsImportMenuOpen(true)}
            />
          </div>
          <SameTimeSetupLinkButton routeOrgId={routeOrgId} variant="row" />
        </>
      )}
      {isLoading ? (
        // Loading skeleton
        Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center gap-2 px-3 py-2.5">
              <div className="h-4 w-4 rounded bg-clovie-surface-2" />
              <div className="h-4 flex-1 rounded bg-clovie-surface-2" />
            </div>
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex items-center gap-2 py-2 pl-9 pr-3">
                <div className="h-4 w-4 rounded bg-clovie-surface-2" />
                <div className="h-4 flex-1 rounded bg-clovie-surface-2" />
              </div>
            ))}
          </div>
        ))
      ) : categories.length === 0 ? (
        <p className="px-5 py-4 text-center text-sm text-clovie-muted max-lg:text-[16px]">
          No service categories found
        </p>
      ) : (
        <SortableServiceCategoryList
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          selectedServiceId={selectedServiceId}
          onCategoryClick={handleCategoryClick}
          onServiceClick={handleServiceClick}
          onAddServiceToCategory={handleAddServiceToCategory}
          onCategoriesReorder={handleCategoriesReorder}
          onServiceReorder={handleServiceReorder}
        />
      )}
    </SettingsPageSidebar>
  );

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Services')}</title>
      </Helmet>

      <PageCanvas className="h-full min-h-0 overflow-hidden">
        <SettingsPageLayout
          sidebar={sidebarContent}
          className="h-full min-h-0 overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg"
          sidebarClassName="calendar-scrollbar border-clovie-border-soft bg-clovie-surface text-clovie-text lg:w-[320px]"
          mainClassName="calendar-scrollbar min-w-0 bg-clovie-surface-2 px-4 py-5 sm:px-6 sm:py-5 lg:px-8 lg:py-6"
          mobileDetailOpen={contentView !== 'default'}
          onMobileBack={handleMobileBack}
          mobileBackLabel="Services"
        >
          {isDesktop && (
            <MenuImportStatusBanner
              currentImport={menuImport.openImport}
              aiEnabled={menuImport.enabled}
              ownerRecipients={menuImport.ownerRecipients}
              routeOrgId={routeOrgId}
              onOpenImportDialog={() => setIsImportMenuOpen(true)}
            />
          )}
          {contentView === 'new-category' ? (
            <NewCategoryForm onCancel={handleCancelNewCategory} onSuccess={handleCategoryCreated} />
          ) : contentView === 'new-service' && selectedCategoryId ? (
            <NewServiceForm
              categoryId={selectedCategoryId}
              categoryName={getSelectedCategoryName()}
              onCancel={handleCancelNewService}
              onSuccess={handleServiceCreated}
            />
          ) : contentView === 'category-detail' && selectedCategory ? (
            <CategoryDetailView
              category={selectedCategory}
              activeTab={activeCategoryTab}
              onTabChange={setActiveCategoryTab}
              onCategoryUpdated={handleCategoryUpdated}
              onCategoryDeleted={handleCategoryDeleted}
            />
          ) : contentView === 'service-detail' && visibleService ? (
            <ServiceDetailView
              key={visibleService.id}
              service={visibleService}
              activeTab={activeServiceTab}
              onTabChange={setActiveServiceTab}
              onServiceUpdated={handleServiceUpdated}
              onServiceDeleted={handleServiceDeleted}
            />
          ) : contentView === 'service-detail' && selectedServiceId && serviceQuery.isError ? (
            <div
              role="alert"
              className="max-w-xl rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 text-clovie-text shadow-soft"
            >
              <p className="text-base text-clovie-text">Couldn't load this service.</p>
              <Button
                variant="outline"
                className="mt-4 min-h-11"
                loading={serviceQuery.isFetching}
                onClick={() => void serviceQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : contentView === 'service-detail' && selectedServiceId ? (
            <ServiceDetailSkeleton />
          ) : menuImport.enabled &&
            !menuImport.currentImport &&
            !isLoading &&
            hasOnlyDemoServices ? (
            <ImportMenuEmptyStateCard
              hasDemoServices={services.length > 0}
              onImport={() => setIsImportMenuOpen(true)}
              onAddByHand={handleAddCategoryClick}
            />
          ) : (
            <div className="max-w-xl rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 text-clovie-text shadow-soft">
              <h1 className="text-2xl font-semibold text-clovie-text">Manage your services</h1>
              <p className="mt-3 text-sm leading-6 text-clovie-muted">
                You can create new service categories and services or change the details (e.g. price
                & duration) of existing services.
              </p>
            </div>
          )}
        </SettingsPageLayout>
      </PageCanvas>

      {isImportMenuOpen && menuImport.enabled && (
        <ImportMenuDialog
          currentImport={menuImport.currentImport}
          onClose={() => setIsImportMenuOpen(false)}
          onDraftReady={handleMenuDraftReady}
        />
      )}
    </>
  );
}
